import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Lock, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
const API_V2 = `${API}/api/v2`;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    if (password !== confirm) {
      toast.error("Les deux mots de passe ne correspondent pas.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_V2}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const d = data.detail;
        const msg = Array.isArray(d) ? d[0]?.msg : d;
        throw new Error(typeof msg === "string" ? msg : "Réinitialisation impossible.");
      }
      toast.success(data.message || "Mot de passe mis à jour.");
      navigate("/connexion", { replace: true });
    } catch (err) {
      toast.error(err.message || "Une erreur est survenue.");
    } finally {
      setLoading(false);
    }
  };

  if (!token || token.length < 20) {
    return (
      <div className="min-h-screen w-full bg-zinc-100 px-4 py-10 pt-[calc(120px+env(safe-area-inset-top,0px))] sm:pt-[calc(128px+env(safe-area-inset-top,0px))] md:pt-[calc(128px+env(safe-area-inset-top,0px))] lg:pt-[calc(132px+env(safe-area-inset-top,0px))]">
        <div className="mx-auto w-full max-w-md rounded-2xl border border-amber-200 bg-amber-50/90 p-6 text-center sm:p-8">
          <p className="text-sm font-medium text-amber-900">Lien invalide ou incomplet.</p>
          <Link
            to="/mot-de-passe-oubli"
            className="mt-4 inline-block text-sm font-bold text-[#190276] hover:underline"
          >
            Demander un nouveau lien
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-zinc-100 px-4 py-10 pt-[calc(120px+env(safe-area-inset-top,0px))] sm:pt-[calc(128px+env(safe-area-inset-top,0px))] md:pt-[calc(128px+env(safe-area-inset-top,0px))] lg:pt-[calc(132px+env(safe-area-inset-top,0px))]">
      <div className="mx-auto w-full max-w-md rounded-2xl border border-zinc-200/90 bg-white p-6 shadow-lg shadow-zinc-200/50 sm:p-8">
        <Link
          to="/connexion"
          className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-[#190276] hover:underline"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={2} />
          Connexion
        </Link>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-zinc-900">
          Nouveau mot de passe
        </h1>
        <p className="mt-2 text-[15px] text-zinc-500">Choisissez un mot de passe d&apos;au moins 8 caractères.</p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-5">
          <div>
            <label htmlFor="reset-pw" className="mb-2 block text-sm font-medium text-zinc-700">
              Nouveau mot de passe <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-zinc-400" />
              <Input
                id="reset-pw"
                type={show ? "text" : "password"}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 rounded-xl border-zinc-200 bg-zinc-50/80 pl-11 pr-12 text-[15px] focus-visible:border-[#190276] focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-[#190276]/15"
                required
                minLength={8}
              />
              <button
                type="button"
                onClick={() => setShow((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-2 text-zinc-500 hover:bg-zinc-100"
                aria-label={show ? "Masquer" : "Afficher"}
              >
                {show ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
              </button>
            </div>
          </div>
          <div>
            <label htmlFor="reset-pw2" className="mb-2 block text-sm font-medium text-zinc-700">
              Confirmer <span className="text-red-500">*</span>
            </label>
            <Input
              id="reset-pw2"
              type={show ? "text" : "password"}
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="h-12 rounded-xl border-zinc-200 bg-zinc-50/80 text-[15px] focus-visible:border-[#190276] focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-[#190276]/15"
              required
              minLength={8}
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="h-12 w-full rounded-xl bg-[#190276] font-semibold hover:bg-[#130160]"
          >
            {loading ? "Enregistrement…" : "Enregistrer le mot de passe"}
          </Button>
        </form>
      </div>
    </div>
  );
}
